import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Clock, Disc, Lock } from "lucide-react";
import { BrandLockup } from "@/components/Brand";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtMoney, fmtMoney0, fmtDate } from "@/lib/format";
import { FUND_META, CORE_FUNDS, SPEND_SEGMENTS, paymentBadge, pct } from "@/lib/budget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Badge({ b }) {
  return <span title={b.hint || ""} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${b.cls}`}>{b.label}</span>;
}

export default function BudgetShare() {
  const { token } = useParams();
  const [d, setD] = useState(undefined);

  useEffect(() => {
    // never let this page be indexed
    const m = document.createElement("meta");
    m.name = "robots"; m.content = "noindex, nofollow, noarchive";
    document.head.appendChild(m);
    axios.get(`${API}/public/budget/${token}`).then((r) => setD(r.data)).catch(() => setD(null));
    return () => { document.head.removeChild(m); };
  }, [token]);

  if (d === undefined)
    return <div className="grid min-h-screen place-items-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-neutral-300 border-t-neutral-900" /></div>;

  if (d === null)
    return (
      <div className="grid min-h-screen place-items-center px-4">
        <div className="text-center">
          <Lock className="mx-auto h-10 w-10 text-neutral-400" />
          <h1 className="mt-3 font-display text-2xl font-bold">Not found</h1>
          <p className="mt-1 text-neutral-500">This budget link is invalid or has been revoked.</p>
        </div>
      </div>
    );

  const a = d.artist;
  const albumTitle = (id) => d.albums.find((x) => x.id === id)?.title || d.albums.find((x) => x.id === id)?.slug || "—";
  const marketingIsMin = true; // marketing shown as minimum on contract baselines

  return (
    <div className="min-h-screen bg-[#F7EFDD]/40">
      <header className="border-b border-[#E7DFCC] bg-[#FFFDF8]">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6"><BrandLockup /><span className="text-xs text-neutral-400">Read-only share</span></div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <h1 className="font-display text-4xl font-extrabold tracking-tight">{a.name}</h1>
            {a.entity && <p className="mt-1 text-sm text-neutral-500">{a.entity}</p>}
          </div>
          <div data-testid="share-snapshot" className="flex items-center gap-2 rounded-full border border-[#E7DFCC] bg-[#FFFDF8] px-3 py-1.5 text-xs text-neutral-500">
            <Clock className="h-3.5 w-3.5" /> Snapshot {a.as_of ? fmtDate(a.as_of) : ""} · not a live sync
          </div>
        </div>
        {a.contract_master_count != null && <p className="mt-2 text-sm text-neutral-600">Contract: {a.contract_master_count} masters total.</p>}

        {a.contract_unconfirmed && (
          <span data-testid="share-unconfirmed" className="mt-4 inline-flex items-center gap-1 rounded-full border border-orange-200 bg-orange-50 px-2.5 py-0.5 text-xs font-semibold text-orange-700">
            <AlertTriangle className="h-3.5 w-3.5" /> Needs review — unconfirmed contract
          </span>
        )}

        {/* Committed vs budget */}
        <h2 className="mt-8 font-display text-2xl font-bold">Committed vs budget</h2>
        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500">
          {SPEND_SEGMENTS.map((seg) => <span key={seg.key} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: seg.color }} /> {seg.label}</span>)}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {CORE_FUNDS.map((f) => {
            const fn = a.funds[f]; const budget = a.budget[f];
            const covered = f === "recording" && a.recording_mode === "covered_by_advance";
            const denom = budget || fn.committed_cents;
            return (
              <div key={f} data-testid={`share-fund-${f}`} className="rounded-2xl border border-[#E7DFCC] bg-white p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{FUND_META[f].label}</span>
                  <span className="num-display text-sm">{covered ? "" : fmtMoney0(fn.committed_cents)}<span className="text-neutral-400">{covered ? "None — covered by advance" : ` / ${budget ? fmtMoney0(budget) : (a.spend_tracked ? "—" : "Not tracked")}`}</span></span>
                </div>
                {!covered && (
                  <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-neutral-200/70">
                    {SPEND_SEGMENTS.map((seg) => { const w = pct(fn[seg.key] || 0, denom); return w > 0 ? <div key={seg.key} style={{ width: `${w}%`, backgroundColor: seg.color }} /> : null; })}
                  </div>
                )}
                {!covered && <div className="mt-2 flex flex-wrap gap-x-3 text-[11px] text-neutral-500"><span>Paid {fmtMoney0(fn.paid_cents)}</span><span>Pending {fmtMoney0(fn.pending_cents)}</span><span>Bookmarked {fmtMoney0(fn.bookmarked_cents)}</span></div>}
              </div>
            );
          })}
        </div>

        {/* Albums */}
        {d.albums.length > 0 && (
          <>
            <h2 className="mt-10 font-display text-2xl font-bold">Albums</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {d.albums.map((al) => (
                <div key={al.id} data-testid={`share-album-${al.id}`} className="rounded-2xl border border-[#E7DFCC] bg-white p-4">
                  <h3 className="flex items-center gap-1.5 truncate font-display text-lg font-bold"><Disc className="h-4 w-4 text-neutral-400" /> {al.title || "Untitled"}</h3>
                  <p className="mt-0.5 text-xs text-neutral-500"><span className="uppercase">{al.kind}</span>{al.planned_tracks ? ` · ${al.planned_tracks} tracks` : al.masters ? ` · ${al.masters} masters` : ""}{al.historical ? " · historical" : ""} · {al.tx_count} tx</p>
                  <div className="mt-3 space-y-3">
                    {["recording", "marketing"].map((f) => {
                      const fn = al.funds[f]; const hasAlloc = fn.allocation_cents != null; const denom = hasAlloc ? fn.allocation_cents : fn.committed_cents;
                      return (
                        <div key={f}>
                          <div className="flex items-center justify-between text-xs"><span className="font-medium text-neutral-600">{FUND_META[f].label}</span><span className="num-display text-neutral-500">{fmtMoney0(fn.committed_cents)}<span className="text-neutral-400"> / {hasAlloc ? fmtMoney0(fn.allocation_cents) : "Not allocated"}</span></span></div>
                          <div className="mt-1 flex h-2 overflow-hidden rounded-full bg-neutral-200/70">{SPEND_SEGMENTS.map((seg) => { const w = pct(fn[seg.key] || 0, denom); return w > 0 ? <div key={seg.key} style={{ width: `${w}%`, backgroundColor: seg.color }} /> : null; })}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Needs review */}
        {d.needs_review.length > 0 && (
          <div data-testid="share-needs-review" className="mt-10 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-800"><AlertTriangle className="h-4 w-4" /> Needs review ({d.needs_review.length})</p>
            <ul className="mt-2 space-y-1.5 text-sm text-amber-900/90">{d.needs_review.map((n, i) => <li key={i} className="flex gap-2"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-amber-500" />{n.text}</li>)}</ul>
          </div>
        )}

        {/* Transactions */}
        <h2 className="mt-10 font-display text-2xl font-bold">Transactions</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-[#E7DFCC] bg-white">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead>Description</TableHead>
              <TableHead>Fund</TableHead><TableHead>Album</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {d.transactions.map((t, i) => {
                const b = paymentBadge(t);
                return (
                  <TableRow key={i} data-testid={`share-tx-${i}`} className={t.kind === "transaction" && t.amount_cents != null ? "" : "opacity-70"}>
                    <TableCell className="whitespace-nowrap text-xs text-neutral-500">{t.date ? fmtDate(t.date) : "—"}</TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm font-medium">{t.vendor || "—"}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-sm text-neutral-600" title={t.description || ""}>{t.description || "—"}{t.note && <span title={t.note} className="ml-1 text-neutral-400">ⓘ</span>}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs"><span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: FUND_META[t.fund]?.color }} />{FUND_META[t.fund]?.label || t.fund}</span></TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-neutral-500">{t.album_id ? albumTitle(t.album_id) : (t.is_single ? "Single (unassigned)" : "Not allocated")}</TableCell>
                    <TableCell className="num-display whitespace-nowrap text-right text-sm font-semibold">{fmtMoney(t.amount_cents)}</TableCell>
                    <TableCell><Badge b={b} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
}
